import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildClientYtdPageReportQuery,
  buildYtdPagePerformanceFetchKey,
  formatYtdPerformanceDate,
  normalizeYtdPagePerformancePayload,
  parseYtdPerformanceDateParts,
  unwrapYtdPageRows,
} from "../../lib/utils/ytdPagePerformanceReport"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

const YTD_AGGREGATION_MODES = ["paid_and_organic", "organic_only", "paid_only"]

function ytdArgKey(arg = {}) {
  const { clientId, startDate, endDate, rangeMode } = arg
  return buildYtdPagePerformanceFetchKey(
    clientId,
    formatYtdPerformanceDate(startDate),
    formatYtdPerformanceDate(endDate),
    rangeMode,
  )
}

async function fetchAllYtdPages(clientId, query) {
  const collected = []
  let endpoint = `report/clients/${clientId}/ytd_page?${query}`
  for (let page = 0; page < 20; page += 1) {
    const data = await get(endpoint)
    collected.push(...unwrapYtdPageRows(data))
    const next = data?.paging?.next
    if (!next) break
    endpoint = next
  }
  return collected
}

function buildYtdQuerySpecs({ rangeMode, start, end, parts, socialNetwork }) {
  return YTD_AGGREGATION_MODES.map((aggregationMode) => ({
    required: aggregationMode === "paid_and_organic",
    query: buildClientYtdPageReportQuery(
      rangeMode === "custom"
        ? {
            period: "custom",
            startDate: start,
            endDate: end,
            aggregationMode,
            socialNetwork,
          }
        : {
            period: "month",
            year: parts.year,
            month: parts.month,
            aggregationMode,
            socialNetwork,
          },
    ),
  }))
}

/**
 * GET /api/v1/report/clients/{clientId}/ytd_page
 * Month: ?aggregation_mode=&period=month&year=&month=&limit=
 * Custom range: ?aggregation_mode=&start_date=&end_date=&limit=
 */
export const fetchClientYtdPagePerformance = createAsyncThunk(
  "clientYtdPagePerformance/fetch",
  async (
    { clientId, startDate, endDate, rangeMode, socialNetwork } = {},
    { rejectWithValue, getState },
  ) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }

      const start = formatYtdPerformanceDate(startDate)
      const end = formatYtdPerformanceDate(endDate)
      const parts = parseYtdPerformanceDateParts(end || start)
      if (!start || !end || (rangeMode !== "custom" && !parts)) {
        return rejectWithValue("startDate and endDate are required")
      }

      const fetchKey = buildYtdPagePerformanceFetchKey(clientId, start, end, rangeMode)
      const specs = buildYtdQuerySpecs({ rangeMode, start, end, parts, socialNetwork })
      const pages = await Promise.all(
        specs.map(async (spec) => {
          try {
            return await fetchAllYtdPages(clientId, spec.query)
          } catch (err) {
            if (spec.required) throw err
            return []
          }
        }),
      )
      const rows = pages.flat()
      const socialNetworkTypes = getState().socialNetworkTypes?.types ?? []
      const normalized = normalizeYtdPagePerformancePayload({ data: rows }, socialNetworkTypes)

      return {
        ...normalized,
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey,
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch social goal performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = ytdArgKey(arg)
      return !skipIfFetchKeyLoaded(getState().clientYtdPagePerformance, key)
    },
  },
)

const initialState = {
  pages: [],
  paidOrganicIndex: {},
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function indexToObject(map) {
  if (map instanceof Map) {
    return Object.fromEntries(map.entries())
  }
  return map ?? {}
}

function objectToIndex(obj) {
  const index = new Map()
  if (obj && typeof obj === "object") {
    Object.entries(obj).forEach(([key, value]) => index.set(key, value))
  }
  return index
}

const clientYtdPagePerformanceSlice = createSlice({
  name: "clientYtdPagePerformance",
  initialState,
  reducers: {
    clearClientYtdPagePerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientYtdPagePerformance.pending, (state, action) => {
        const nextKey = ytdArgKey(action.meta.arg ?? {})
        const keepStale =
          nextKey === state.fetchedFetchKey &&
          state.status === "succeeded" &&
          Object.keys(state.paidOrganicIndex || {}).length > 0
        if (!keepStale) {
          state.status = "loading"
        }
        state.error = null
        state.requestedFetchKey = nextKey
      })
      .addCase(fetchClientYtdPagePerformance.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.pages = action.payload?.pages ?? []
        state.paidOrganicIndex = indexToObject(action.payload?.paidOrganicIndex)
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
      })
      .addCase(fetchClientYtdPagePerformance.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch social goal performance"
      })
  },
})

export const { clearClientYtdPagePerformance } = clientYtdPagePerformanceSlice.actions
export default clientYtdPagePerformanceSlice.reducer

export function selectPaidOrganicPerformanceIndex(state) {
  return objectToIndex(state.clientYtdPagePerformance?.paidOrganicIndex)
}
